const RawExternalDataPGAdapter = require('../../common/raw-external-pg-adapter')
const PGAdapter = require('../../common/pg-adapter')
const { transformWarehouseData } = require('../tools/transform-rms-stock')
const { WAREHOUSE_DEFINITIONS } = require('../../order/tools/read/warehouse-code-to-virtual')

const columnPreparers = {
  created_at: (key, value) => {
    return [{
      statement: `"${key}">=%INDEX%::date`,
      value
    }]
  }
}
class RMSStockToAvocadoPGAdapter {
  constructor (api, pgConnection, logger, username) {
    this.api = api
    this.pgConnection = pgConnection
    this.logger = logger
    this.username = username

    this.rawDataAdapter = new RawExternalDataPGAdapter({
      pgConnection,
      username,
      logger
    })

    // Adapter for data_ext_stockevent table
    this.stockEventAdapter = new PGAdapter(
      pgConnection,
      'avocado.data_ext_stockevent',
      username,
      ['id', 'location_id', 'organisation_id', 'sku', 'date', 'closing', 'source', 'created_at', 'updated_at', 'created_by', 'updated_by'],
      'id',
      columnPreparers,
      logger
    )

    // Adapter for data_ext_stockevent_detail table
    this.stockEventDetailAdapter = new PGAdapter(
      pgConnection,
      'avocado.data_ext_stockevent_detail',
      username,
      ['id', 'stock_event_id', 'batch', 'expiry_date', 'program', 'quantity', 'manufacturer', 'created_at', 'updated_at'],
      'id',
      columnPreparers,
      logger
    )
  }

  /**
   * Check if raw stock data already exists for a given organisation and date
   * @param {String} organisationId - Organisation ID
   * @param {String} date - Date string in YYYY-MM-DD format
   * @param {Object} options - Options including client for transaction support
   * @returns {Boolean} - True if data already exists
   */
  async hasRawDataForDate (organisationId, date, options = {}) {
    const client = options.client || this.pgConnection
    const result = await client.query(
      `SELECT 1 FROM raw.external_stock_data
       WHERE organisation_id = $1
         AND data->>'ingestDate' = $2
       LIMIT 1`,
      [organisationId, date]
    )
    return result.rows.length > 0
  }

  /**
   * Sync RMS raw stock data into raw.external_stock_data table
   * @param {String} organisationId - Organisation ID
   * @param {Object} rmsStockData - Raw RMS stock data
   * @param {Object} options - Options including client for transaction support
   */
  async syncRMSRawData (organisationId, rmsStockData, options = {}) {
    try {
      // Store in raw table - just add fetch timestamp and request params
      const dataToStore = rmsStockData.items.map(item => ({
        organisation_id: organisationId,
        data: item
      }))

      const savedRecords = await this.rawDataAdapter.upsertBatchWithJsonbPaths(dataToStore, {
        conflictIdColumns: ['organisation_id'],
        conflictJsonbPaths: [
          { column: 'data', key: 'sapCode' },
          { column: 'data', key: 'program' },
          { column: 'data', key: 'valuationArea' },
          { column: 'data', key: 'batchNumber' },
          { column: 'data', key: 'ingestDate' }
        ],
        batchSize: 2000,
        client: options.client
      })

      this.logger.info('DEBUG: Raw data upsert completed', {
        savedRecordsCount: savedRecords.length,
        sampleSavedRecord: savedRecords[0]
      })

      const savedRecordIds = savedRecords.map(record => record.id)

      return {
        success: true,
        total: savedRecords.length,
        savedRecordIds
      }
    } catch (error) {
      this.logger.error('Failed to fetch and store RMS data', {
        error: error.message,
        organisationId
      })
      throw error
    }
  }

  /**
   * Transform RMS stock data to data_ext_stockevent format
   */
  async transformDailyRawDocs (organisationId, date, options = {}) {
    if (!organisationId) {
      throw new Error('organisationId is required')
    }
    // Get all raw records for the organisation for the given date
    const allRecordsFromToday = await this.rawDataAdapter.getByOrganisation(organisationId, date, options)

    this.logger.info(`Found ${allRecordsFromToday.length} records for ${date}`)

    if (allRecordsFromToday.length === 0) {
      return {
        success: true,
        totalRecords: allRecordsFromToday.length
      }
    }

    let locationUuidById = {}
    const rmsDefinitions = WAREHOUSE_DEFINITIONS.RMS || {}
    const rmsLocationIds = Object.values(rmsDefinitions)
      .map(def => def && def._id)
      .filter(Boolean)
    if (rmsLocationIds.length) {
      try {
        const { results } = await this.api.location.pgAdapter.getBulk('fsid, uuid', 'fsid', rmsLocationIds)
        locationUuidById = (results || []).reduce((acc, row) => {
          if (row && row.fsid && row.uuid) {
            acc[row.fsid] = row.uuid
          }
          return acc
        }, {})
      } catch (error) {
        this.logger.warn('Failed to resolve RMS warehouse UUIDs from PG', {
          error: error.message
        })
      }
    }

    const transformedDocs = transformWarehouseData(
      organisationId,
      allRecordsFromToday,
      this.username,
      { locationUuidById }
    )
    return transformedDocs
  }

  /**
   * Sync RMS transformed stock data into data_ext_stockevent table
   * @param {String} organisationId - Organisation ID
   * @param {Date} date - Date to filter raw records
   * @param {Object} options - Optional parameters including client for transactions
   */
  async syncTransformedStockData (organisationId, date = new Date(), options = {}) {
    const formattedDate = date.toISOString().split('T')[0]
    if (!organisationId) {
      throw new Error('organisationId is required')
    }
    try {
      const transformedRecords = await this.transformDailyRawDocs(organisationId, formattedDate, options)
      if (transformedRecords.length === 0) {
        return this.logger.info('No transformed records to sync', {
          organisationId,
          date
        })
      }

      // Separate details from headers
      const detailsToSave = []
      const headersToSave = transformedRecords.map(record => {
        const { _details, ...header } = record
        detailsToSave.push(..._details)
        return header
      })

      const savedStockResponse = await this.stockEventAdapter.bulkUpsert(headersToSave, {
        conflictIdColumns: ['sku', 'date', 'source'],
        client: options.client,
        batchSize: 2000
      })

      const savedStockDetailsResponse = await this.stockEventDetailAdapter.bulkUpsert(detailsToSave, {
        conflictIdColumns: ['stock_event_id', 'batch', 'program'],
        client: options.client,
        batchSize: 2000
      })
      this.logger.info('Successfully synced transformed stock data', {
        organisationId,
        date,
        totalRecords: transformedRecords.length,
        upsertedRecords: savedStockResponse.length,
        detailsRecords: savedStockDetailsResponse.length
      })
      return savedStockResponse
    } catch (error) {
      this.logger.error('Failed to sync transformed stock data', {
        error: error.message,
        organisationId,
        date
      })
      throw error
    }
  }

  /**
   * Get stock event rows for analytics transform
   */
  async getStockEventAnalyticsRows ({ organisationId, locationId, date }) {
    const query = `
      SELECT
        h.location_id,
        h.source,
        h.sku,
        h.date,
        d.program,
        d.batch,
        d.expiry_date,
        d.quantity,
        d.manufacturer,
        f.daily_forecast * 30 AS month_forecast,
        f.safety_stock,
        f.reorder_point,
        f.max_level
      FROM avocado.data_ext_stockevent h
      JOIN avocado.data_ext_stockevent_detail d
        ON h.id = d.stock_event_id
      LEFT JOIN avocado.data_location loc
        ON loc.uuid = h.location_id::text
      LEFT JOIN avocado.vw_supply_plan_values f
        ON h.sku = f.sku
        AND loc.fsid = f.location
        AND f.program = 'program:' || LOWER(d.program)
      WHERE h.organisation_id = $1
        AND h.date::date = $2
        AND h.location_id = $3
    `
    const { rows } = await this.pgConnection.query(query, [organisationId, date, locationId])
    return rows
  }
}

module.exports = RMSStockToAvocadoPGAdapter
